import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { Tool } from '@procureai/agents';
import { useTranslation } from 'react-i18next';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import {
  SvbrgContractItem,
  SvbrgSearchContractItemInput,
  SvbrgSearchContractItemOutput,
} from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { useContractItemResultDataGrid } from '~/routes/IntakeChat/components/ContractItemSearchResult/ContractItemSearchResult.dataGrid';

import { ToolResultComponent } from '.';
import { isWildcardOrEmptyQuery } from './toolLabel.utils';

const collectItems = (output: SvbrgSearchContractItemOutput): SvbrgContractItem[] =>
  (output.groupedItems ?? []).flatMap((group) => group.items);

export const ContractItemTool: ToolResultComponent<
  Tool.searchContractItem,
  SvbrgSearchContractItemInput,
  SvbrgSearchContractItemOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });

  const config = useContractItemResultDataGrid();
  const apiRef = useGridApiRef();

  const query = tool.input?.query;
  const isNoQuery = isWildcardOrEmptyQuery(query);
  const callMessage = isNoQuery ? t('callNoQuery') : t('call', { query });

  switch (tool.state) {
    case 'input-available':
    case 'input-streaming':
      return <ChatLoadingIndicator message={callMessage} />;

    case 'output-available': {
      const items = collectItems(tool.output);
      const resultMessage = isNoQuery
        ? t('resultNoQuery', { count: items.length })
        : t('result', { count: items.length, query });

      // Zero-result case: render a non-interactive indicator. The agent's preamble describes
      // the absence in text when items.length === 0. Mirrors BUG-2062 fix for POs.
      if (items.length === 0) {
        return <ChatToolResultIndicator message={resultMessage} defaultOpen={false} disabled={true} />;
      }

      return (
        <ChatToolResultIndicator message={resultMessage} defaultOpen={false} disabled={false}>
          <DataGridPro
            {...config}
            sx={{
              [`&.${gridClasses.root}`]: {
                '--DataGrid-containerBackground': 'transparent',
              },
            }}
            apiRef={apiRef}
            disableColumnMenu
            rows={items}
            getRowId={(row) => row.id}
          />
        </ChatToolResultIndicator>
      );
    }
  }
};
